// SkillX batch leaderboard — design tokens, the level ladder, and the badge
// catalogue for the chief-mentor view.
//
// XP and badge keys are REAL: they come from the XP ledger over
// /student-portal/skillx/leaderboard/* (see public/docs/SKILLX_XP_LEADERBOARD.md).
// Rank and level are derived here on the frontend from `xp` alone, exactly as
// the student portal derives them, so the two portals agree without the
// backends having to ship level names.
//
// Palette, levels and badges are transcribed from the student portal
// (src/sections/skillx/skillxTokens.js and leaderboard/leaderboardData.js) so
// the two areas read as one product. Keep them in step: the level ladder and
// the badge keys are a cross-repo contract, documented in
// SKILLX_XP_LEADERBOARD.md §7.

// ----- design tokens (mirror of the student portal skillxTokens) -----------
export const SX = {
  blue: '#2E75BC',
  cyan: '#14A0DA',
  purple: '#7C4E9F',
  primary: '#2E75BC',
  brandGradient: 'linear-gradient(135deg, #2E75BC 0%, #7C4E9F 100%)',
  heroGradient: 'linear-gradient(115deg, #2E75BC 0%, #14A0DA 42%, #7C4E9F 100%)',
  text: '#1B2438',
  textMuted: '#66708A',
  card: '#FFFFFF',
  cardSoft: '#F5F8FD',
  border: 'rgba(46, 117, 188, 0.16)',
  borderStrong: 'rgba(27, 36, 56, 0.10)',
  borderActive: '#2E75BC',
  radius: { sm: 12, md: 16, lg: 20 },
  shadow: '0 8px 24px rgba(27, 36, 56, 0.10)',
  shadowSoft: '0 4px 14px rgba(27, 36, 56, 0.07)',
  shadowHover: '0 16px 34px rgba(27, 36, 56, 0.14)',
};

// ----- level ladder ---------------------------------------------------------
// Nine tiers, per the product spec. The thresholds are the documented ones
// (SKILLX_XP_LEADERBOARD.md §7) — retune `min` here if the cut-offs change,
// and mirror the change in the student portal's LEVELS.
export const LEVELS = [
  { key: 'starter', name: 'Starter', min: 0, color: '#7A8699', icon: 'solar:leaf-bold-duotone' },
  { key: 'explorer', name: 'Explorer', min: 150, color: '#14A0DA', icon: 'solar:compass-bold-duotone' },
  { key: 'achiever', name: 'Achiever', min: 350, color: '#2E75BC', icon: 'solar:medal-ribbon-bold-duotone' },
  { key: 'professional', name: 'Professional', min: 650, color: '#5A63AE', icon: 'solar:shield-star-bold-duotone' },
  { key: 'expert', name: 'Expert', min: 1000, color: '#7C4E9F', icon: 'solar:diploma-verified-bold-duotone' },
  { key: 'master', name: 'Master', min: 1500, color: '#C25E82', icon: 'solar:crown-bold-duotone' },
  { key: 'digital_ninja', name: 'Digital Ninja', min: 2200, color: '#2E7D8F', icon: 'solar:bolt-bold-duotone' },
  { key: 'skill_champion', name: 'Skill Champion', min: 3000, color: '#C25E00', icon: 'solar:cup-star-bold-duotone' },
  { key: 'interval_legend', name: 'Interval Legend', min: 4000, color: '#D4A017', icon: 'solar:crown-star-bold-duotone' },
];

/** The tier an XP total lands in — the highest tier whose `min` it clears. */
export const levelFor = (xp = 0) => [...LEVELS].reverse().find((l) => xp >= l.min) || LEVELS[0];

/** The tier above the current one, or null at the top of the ladder. */
export const nextLevelFor = (xp = 0) => LEVELS.find((l) => xp < l.min) || null;

/**
 * Progress through the current tier: `pct` (0..100) for a bar, `toNext` XP
 * still to earn, and the tier being climbed towards. At Interval Legend there
 * is nothing above, so the bar reads full.
 */
export const levelProgress = (xp = 0) => {
  const current = levelFor(xp);
  const next = nextLevelFor(xp);
  if (!next) return { current, next: null, pct: 100, toNext: 0 };

  const span = next.min - current.min;
  const gained = xp - current.min;
  return {
    current,
    next,
    pct: span > 0 ? Math.min(100, Math.max(0, Math.round((gained / span) * 100))) : 0,
    toNext: Math.max(0, next.min - xp),
  };
};

// ----- badge catalogue ------------------------------------------------------
// The 11 achievement badges from the spec (public/assets/badges.png). Each
// `key` is the snake_case of its name and is the CONTRACT with the API: the
// backend returns earned keys, the name/icon/colour are rendered from here.
//
// Badge awarding will move to the badges backend; several of these (SEO / Meta
// Ads / Google Ads / AI / Placement) depend on module and certification signals
// that are not XP rules and so cannot be derived from the ledger at all. Until
// then the leaderboard controller awards only the six the ledger can prove —
// see BADGE_RULES in skillxLeaderboard.controller.js.
export const BADGES = [
  { key: 'perfect_attendance', name: 'Perfect Attendance', description: 'A full month without missing a class', icon: 'solar:calendar-mark-bold-duotone', color: '#2E75BC' },
  { key: 'assignment_champion', name: 'Assignment Champion', description: 'Submitted 10 assignments', icon: 'solar:notebook-bold-duotone', color: '#2E7D62' },
  { key: 'seo_master', name: 'SEO Master', description: 'Completed the SEO module', icon: 'solar:magnifer-bold-duotone', color: '#7C4E9F' },
  { key: 'meta_ads_expert', name: 'Meta Ads Expert', description: 'Completed the Meta Ads module', icon: 'solar:target-bold-duotone', color: '#C25E82' },
  { key: 'google_ads_pro', name: 'Google Ads Pro', description: 'Completed the Google Ads module', icon: 'solar:global-bold-duotone', color: '#14A0DA' },
  { key: 'ai_explorer', name: 'AI Explorer', description: 'Completed the AI tools module', icon: 'solar:cpu-bolt-bold-duotone', color: '#5A63AE' },
  { key: 'presentation_star', name: 'Presentation Star', description: 'Delivered 3 classroom presentations', icon: 'solar:presentation-graph-bold-duotone', color: '#B7791F' },
  { key: 'consistency_champion', name: 'Consistency Champion', description: 'Kept a learning streak going', icon: 'solar:fire-bold-duotone', color: '#C25E00' },
  { key: 'fast_learner', name: 'Fast Learner', description: 'Submitted 5 assignments ahead of the due date', icon: 'solar:rocket-2-bold-duotone', color: '#2E7D8F' },
  { key: 'community_helper', name: 'Community Helper', description: 'Helped 5 classmates in the batch', icon: 'solar:users-group-rounded-bold-duotone', color: '#2E7D62' },
  { key: 'placement_ready', name: 'Placement Ready', description: 'Cleared the placement readiness review', icon: 'solar:case-round-bold-duotone', color: '#D4A017' },
];

export const BADGES_BY_KEY = BADGES.reduce((map, b) => ({ ...map, [b.key]: b }), {});

export const MEDALS = {
  1: { color: '#D4A017', label: '1st', icon: 'solar:cup-star-bold-duotone', glow: 'rgba(212,160,23,0.35)' },
  2: { color: '#8E9AAF', label: '2nd', icon: 'solar:medal-ribbon-bold-duotone', glow: 'rgba(142,154,175,0.32)' },
  3: { color: '#B87333', label: '3rd', icon: 'solar:medal-ribbon-bold-duotone', glow: 'rgba(184,115,51,0.30)' },
};

// Solid accents cycled across the top-performer cards. The cards themselves are
// white — the accent only tints the icon tile, the avatar ring and the top
// hairline, so a row of batches reads as one set without shouting.
export const CARD_ACCENTS = ['#2E75BC', '#7C4E9F', '#14A0DA', '#2E7D62', '#C25E00', '#5A63AE'];

// ----- helpers --------------------------------------------------------------
export const initialsOf = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

/* ------------------------------------------------------------------ *
 * The leaderboard API returns { id, name, image, xp, badges } per learner.
 * XP and badges are real (from the XP ledger); level is derived here from xp.
 * ------------------------------------------------------------------ */

/** Map one API student to the row shape the cards and table render. */
export const toLeaderboardRow = (s = {}) => {
  const xp = Number(s.xp) || 0;
  return {
    id: s.id,
    name: s.name || 'Unnamed learner',
    admissionNo: s.id ?? null,
    image: s.image || null,
    xp,
    level: levelFor(xp),
    badges: Array.isArray(s.badges) ? s.badges : [],
  };
};

/** Map + sort by XP + assign dense-tie ranks (1, 2, 2, 4). */
export const rankApiStudents = (students = []) => {
  const rows = students.map(toLeaderboardRow).sort((a, b) => b.xp - a.xp);

  let lastXp = null;
  let lastRank = 0;
  return rows.map((student, i) => {
    const rank = student.xp === lastXp ? lastRank : i + 1;
    lastXp = student.xp;
    lastRank = rank;
    return { ...student, rank };
  });
};
